import React, {useState} from 'react';
import styles from './CreateClientProjectRequest.module.css';
import FileInput from '../../common/input/FileInputPanel';
import TextCard from '../../common/textCard/TextCard';
import {useNavigate} from "react-router-dom";
import {ProjectType} from "../../../types/project";
import {ProjectRequest} from "../../../types/client";
import {createProjectRequestAPI} from "../../../services/client";
import {useAlert} from "../../common/error/AlertContext";
import Button from "../../common/button/Button";

// 컴포넌트 내부용 타입 (Date 객체 사용)
interface InternalProjectRequest {
    projectName: string;
    deadline: Date;
    startAt: Date;
    explanation: string;
    dataAmount: number;
    projectType: ProjectType;
}

// 프로젝트 추가 요청 생성
const RequestProjectForm: React.FC = () => {
    const navigate = useNavigate();
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);

    // 내부적으로는 Date 객체로 관리
    const [formData, setFormData] = useState<InternalProjectRequest>({
        projectName: '',
        deadline: new Date(),
        startAt: new Date(),
        explanation: '',
        dataAmount: 0,
        projectType: ProjectType.POLYGON
    });

    const {showAlert} = useAlert();

    // Date 객체를 YYYY-MM-DD 문자열로 변환 (로컬 시간대 기준)
    //날짜 데이터 형식 변환, 없으면 '-', 있으면 시간 데이터 제외
    const formatDateToString = (date: Date | string): string => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);

        if (isNaN(dateObj.getTime())) {
            return '-';
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Date 객체를 화면 표시용 문자열로 변환 (로컬 시간대 기준)
    const formatDateForDisplay = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // API 전송용 데이터 변환
    const getApiFormData = (): ProjectRequest => ({
        projectName: formData.projectName,
        deadline: formatDateToString(formData.deadline),
        startAt: formatDateToString(formData.startAt),
        explanation: formData.explanation,
        dataAmount: formData.dataAmount,
        projectType: formData.projectType
    });

    //업로드 가능한 파일 확장자
    const ALLOWED_IMAGE_TYPES = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/bmp',
        'image/webp'
    ];

    //확장자 검수
    const validateImageFile = (file: File): boolean => {
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            showAlert('이미지 파일만 업로드 가능합니다.', "error");
            return false;
        }
        return true;
    };

    //파일 수정
    const handleFileSelect = (file: File) => {
        if (!validateImageFile(file)) return;
        setUploadedFile(file);
    };

    //프로젝트 추가 요청 생성
    const handleSubmit = async () => {
        if (!formData.projectName.trim()) {
            showAlert('프로젝트 이름을 입력해주세요.', "error");
            return;
        }
        try {
            // Date 객체를 문자열로 변환하여 전송
            const requestData = getApiFormData();

            //프로젝트 추가 요청 생성 API
            await createProjectRequestAPI(requestData, uploadedFile);
            showAlert("프로젝트 요청이 완료되었습니다", "info");
            //프로젝트 테이블 페이지로 이동
            navigate("/client/projects");
        } catch (error) {
            console.error('Error submitting form:', error);
        }
    };

    //프로젝트 정보
    const projectDetails = [
        //데이터 수량 선택
        {
            label: "데이터 수량",
            value: formData.dataAmount.toLocaleString(),
            type: "text" as const,
            onChange: (value: string) => setFormData(prev => ({
                ...prev,
                dataAmount: parseInt(value) || 0
            }))
        },
        //가공방식 선택
        {
            label: "가공 방식",
            value: formData.projectType,
            type: "select" as const,
            options: Object.values(ProjectType),
            onChange: (value: string) => setFormData(prev => ({
                ...prev,
                projectType: value as ProjectType
            }))
        }
    ];

    //프로젝트 날짜 정보
    const projectDates = [
        //작업 시작일 선택
        {
            label: "희망 작업 시작일",
            value: formatDateForDisplay(formData.startAt),
            type: "date" as const,
            onChange: (value: string) => setFormData(prev => ({
                ...prev,
                startAt: new Date(value)
            }))
        },
        //작업 종료일 선택
        {
            label: "희망 작업 종료일",
            value: formatDateForDisplay(formData.deadline),
            type: "date" as const,
            onChange: (value: string) => setFormData(prev => ({
                ...prev,
                deadline: new Date(value)
            }))
        }
    ];

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div>
                    <h1 className={styles.title}>Request New Project</h1>
                    <p className={styles.subtitle}>프로젝트 추가 요청</p>
                </div>
                <div className={styles.actions}>
                    {/*요청 버튼*/}
                    <Button
                        variant={"primary"}
                        onClick={handleSubmit}
                    >
                        저장
                    </Button>
                    {/*취소 버튼, 프로젝트 테이블 페이지로 이동*/}
                    <Button
                        variant={"secondary"}
                        onClick={() => navigate("/client/projects")}
                    >
                        취소
                    </Button>
                </div>
            </div>

            <div className={styles.content}>
                <div className={styles.leftSection}>
                    <div className={styles.uploadSection}>
                        {/*샘플 데이터 업로드*/}
                        <FileInput
                            onFileSelect={handleFileSelect}
                            accept={ALLOWED_IMAGE_TYPES.join(',')}
                            maxSize={5 * 1024 * 1024}
                            className={styles.calc}
                        />
                    </div>

                    <div className={styles.detailsCard}>
                        {/*프로젝트 정보*/}
                        <TextCard items={projectDetails}/>
                        <TextCard items={projectDates}/>
                    </div>
                </div>

                <div className={styles.rightSection}>
                    <div className={styles.inputGroup}>
                        {/*프로젝트명 입력*/}
                        <input
                            type="text"
                            className={styles.projectNameInput}
                            placeholder="Project Name"
                            value={formData.projectName}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                projectName: e.target.value
                            }))}
                        />
                    </div>

                    <div className={styles.inputGroup}>
                        {/*프로젝트 설명 입력*/}
                        <textarea
                            className={styles.projectExplanation}
                            placeholder="Project Explanation"
                            value={formData.explanation}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                explanation: e.target.value
                            }))}
                            rows={5}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RequestProjectForm;